/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast.  Keeping your custom
 * Javascript in this fill will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

(function () {

  class ProductPurchaseNotice extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.noticeText = this.querySelector('[data-purchase-notice-text]');
      this.defaultNotice = this.querySelector('[data-purchase-notice-default]').content.textContent.trim();
      this.notices = new Map(Array.from(this.querySelectorAll('[data-purchase-notice-variant]'), (template) => [
        template.dataset.purchaseNoticeVariant,
        template.content.textContent.trim(),
      ]));

      const section = this.closest('[data-section-type="product"]');
      const selectedId = section?.querySelector('[data-form-wrapper] input[name="id"]')?.value;
      this.updateNotice(selectedId || this.dataset.initialVariantId);

      section?.addEventListener('theme:variant:change', (event) => {
        if (event.target === section) this.updateNotice(event.detail.variant?.id);
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    updateNotice(variantId) {
      const id = String(variantId);
      const notice = this.notices.has(id) ? this.notices.get(id) || this.defaultNotice : '';
      this.noticeText.textContent = notice;
      this.hidden = notice.length === 0;
    }
  }

  if (!customElements.get('product-purchase-notice')) {
    customElements.define('product-purchase-notice', ProductPurchaseNotice);
  }

  // Custom elements also initialize after a Theme Editor section reload.
  class ProductStyleOptions extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.querySelectorAll('.product-style__item').forEach((item) => {
        const link = item.querySelector('.product-style__link');
        if (!item.querySelector('.product-style__preview')) return;

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch' && window.matchMedia('(hover: hover)').matches) {
            this.showPreview(item);
          }
        }, { signal });

        item.addEventListener('pointerleave', () => {
          if (this.activeItem === item && !link.matches(':focus-visible')) this.hidePreview();
        }, { signal });

        link.addEventListener('focus', () => {
          if (link.matches(':focus-visible')) this.showPreview(item);
        }, { signal });

        link.addEventListener('blur', () => {
          if (this.activeItem === item) this.hidePreview();
        }, { signal });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.hidePreview();
      }, { signal });
      window.addEventListener('resize', () => this.hidePreview(), { signal });
    }

    disconnectedCallback() {
      this.hidePreview();
      this.controller?.abort();
    }

    showPreview(item) {
      this.hidePreview();
      const preview = item.querySelector('.product-style__preview');
      preview.style.setProperty('--style-preview-shift', '0px');
      item.classList.toggle('is-preview-below', item.getBoundingClientRect().top < preview.offsetHeight + 16);

      // Keep previews at either edge of the product column inside the viewport.
      const bounds = preview.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const shift = Math.max(8 - bounds.left, 0) - Math.max(bounds.right - viewportWidth + 8, 0);
      preview.style.setProperty('--style-preview-shift', `${shift}px`);
      item.classList.add('is-preview-open');
      this.activeItem = item;
    }

    hidePreview() {
      this.activeItem?.classList.remove('is-preview-open');
      this.activeItem = null;
    }
  }

  if (!customElements.get('product-style-options')) {
    customElements.define('product-style-options', ProductStyleOptions);
  }

  // Announcement link 
  document.addEventListener("DOMContentLoaded", function () {
    const announcementMessages = document.querySelectorAll('.announcement__message');
    announcementMessages.forEach(function (message) {
      message.addEventListener('click', function () {
        const url = this.getAttribute("data-link");
        window.location.href = url;
      });
    });
  });


  // Header nav hover
  const grandLinks = document.querySelectorAll('[data-grand-link]');
  grandLinks.forEach(link => {
    link.addEventListener('mouseenter', function () {
      const dropdownFamily = this.closest('.dropdown__family');
      const navlinkItems = dropdownFamily.querySelectorAll('.navlink-item');
      navlinkItems.forEach(item => {
        item.classList.remove('active');
      });
      this.closest('.navlink-item').classList.add('active');
    });
  });


  const selectors$A = {
    dropMenuWrapper: "[data-drop-menu-wrapper]",
    dropMenuToggle: "[data-drop-menu-toggle]",
    dropMenu: "[data-drop-menu-content]",
    dropMenuIcon: "[data-drop-menu-icon]",
  };

  // Dropdown Menu
  class DropdownMenu {
    constructor(dropWrapper) {
      this.wrapper = dropWrapper;
      this.toggle = this.wrapper.querySelector(selectors$A.dropMenuToggle);
      this.content = this.wrapper.querySelector(selectors$A.dropMenu);
      this.icon = this.wrapper.querySelector(selectors$A.dropMenuIcon);
      this.isExpanded = false;

      this.init();
    }
    init() {
      this.toggle.addEventListener('click', () => this.toggleMenu());
    }
    toggleMenu() {
      this.isExpanded ? this.collapse() : this.expand();
    }
    expand() {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.classList.add('active');
      this.icon.style.transform = 'rotate(180deg)';
      this.isExpanded = true;
      this.animateExpand();
    }
    collapse() {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.style.transition = 'max-height 0.3s ease-in';
      this.content.classList.remove('active');
      this.icon.style.transform = 'rotate(0deg)';
      this.isExpanded = false;
      this.animateCollapse();
    }
    animateExpand() {
      this.content.style.transition = 'max-height 0.3s ease-out';
      setTimeout(() => {
        this.content.style.maxHeight = null;
      }, 300);
    }
    animateCollapse() {
      setTimeout(() => {
        this.content.style.maxHeight = '0px';
      }, 0);
    }
  }
  document.addEventListener('DOMContentLoaded', function () {
    const menuWrappers = document.querySelectorAll(selectors$A.dropMenuWrapper);
    menuWrappers.forEach(wrapper => new DropdownMenu(wrapper));
  });

  // Timeline Swiper
  const timelineSwiper = new Swiper("#timeline-swiper", {
    slidesPerView: 1.3,
    spaceBetween: 2,
  })

  // Show more button
  const showMoreButton = document.querySelector("[data-expand-more]");
  const showMoreItems = document.querySelectorAll("[data-expand-more-item]");
  const hideClass = "hide-mb";
  if (showMoreButton) {
    showMoreButton.addEventListener("click", function () {
      showMoreItems.forEach(item => item.classList.remove(hideClass));
      this.style.display = "none";
    });
  }

  // Search color switcher
  const selectors$BA = {
    collectionLoop: ".collection-products",
    productCard: ".product-item",
    colorList: "[data-color-swatch]",
    colorButton: "[data-color-swatch-item]",
    productImage: ".product-item__image",
    productSubtitle: ".product-item__subtitle",
    productColor: ".product-item__color",
    searchelement: "[data-predictive-search-results]",
  };

  class SearchColorVariantSwitcher {
    constructor(searchContainer) {
      this.searchContainer = searchContainer;
      this.init();
    }

    init() {
      this.searchContainer.addEventListener('click', (e) => this.handleClick(e));
    }

    handleClick(e) {
      const button = e.target.closest(selectors$BA.colorButton);
      if (!button || !button.classList.contains('color-btn')) return;

      const colorList = button.closest(selectors$BA.colorList);
      const colorButtons = colorList.querySelectorAll(selectors$BA.colorButton);
      const productCard = colorList.closest(selectors$BA.productCard);
      const productImage = productCard.querySelector(selectors$BA.productImage);
      const priceElement = productCard.querySelector('.product-price');

      this.switchVariant(button, colorButtons, productCard, productImage);
    }

    switchVariant(button, colorButtons, productCard, productImage) {
      const variantLink = button.getAttribute('data-variant-link');
      const variantId = button.getAttribute('data-variant-id');
      const variantPrice = button.getAttribute('data-variant-price');
      const variantComparePrice = button.getAttribute('data-variant-compare-price');
      const discount = button.getAttribute('data-variant-discount');
      const isOnSale = !!discount;

      productCard.querySelectorAll('.product-link').forEach((link) => {
        link.href = variantLink;
      });

      productImage.querySelectorAll(".product-item__bg__inner").forEach((image) => {
        image.classList.remove('is-visible');
      });
      productImage.querySelector(`.product-item__bg__inner[data-variant-id='${variantId}']`).classList.add('is-visible');

      productCard.querySelector('.price__sale-value').classList.toggle('on-sale', isOnSale);
      productCard.querySelector('.price__sale-value').textContent = variantPrice || '';
      productCard.querySelector('.price__sale-compare').textContent = variantComparePrice || '';
      productCard.querySelector('.price__discount').innerHTML = discount ? `(<span class="discount-num">${discount}</span>% off)` : '';

      colorButtons.forEach((btn) => {
        btn.classList.remove('active');
      });
      button.classList.add('active');
    }
  }

  document.querySelectorAll('[data-predictive-search-results]').forEach((search) => new SearchColorVariantSwitcher(search));


  document.querySelector('.product-upsell-checkbox-submit')?.addEventListener('change', function () {
    var checkbox = this;
    var submitButton = this.parentNode.querySelector('button[type="submit"]');
    if (checkbox.checked) {
      submitButton.click();
    }
  });
  document.querySelector('.product-upsell-checkbox-variants')?.addEventListener('change', function () {
    var checkbox = this;
    var button = this.parentNode.querySelector('button[type="button"]');
    if (checkbox.checked) {
      button.click();
    }
  });


  function updatePromotionalMessage(variantId) {
    var message = window.variant_promotional_messages[variantId] || '';
    if (!message) {
      message = window.product_promotional_message;
    }
    var promoBlock = document.querySelector('.promotional-messaging');
    var blockWrapper = promoBlock && promoBlock.closest('.product__block');
    if (promoBlock && blockWrapper) {
      promoBlock.querySelector('p').textContent = message || '';
      if (!message) {
        blockWrapper.style.display = 'none';
      } else {
        blockWrapper.style.display = 'block';
      }
    }
  }

  document.querySelectorAll('[data-single-option-selector]').forEach(function (input) {
    input.addEventListener('change', function () {
      var idInput = document.querySelector('input[name="id"]');
      if (idInput) {
        var variantId = idInput.value;
        updatePromotionalMessage(variantId);
      }
    });
  });

})();
